import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";

const FERRARI_IMG =
  "https://customer-assets-39nsmqrw.emergentagent.net/job_lifeplan-schedule/artifacts/yyousbk2_image0%20%282%29.webp";

/**
 * Signature Ferrari statement section — full-bleed image with parallax
 * and a bold pull quote overlay. This is the "wow" moment between the
 * marquee and the manifesto.
 */
export default function FerrariStatement() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [-80, 120]);
  const opacity = useTransform(scrollYProgress, [0, 0.3, 0.7, 1], [0, 1, 1, 0.4]);

  return (
    <section
      ref={ref}
      className="relative h-[90vh] md:h-screen bg-ink overflow-hidden"
      data-testid="ferrari-statement"
    >
      <motion.div style={{ y }} className="absolute inset-0 will-change-transform">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${FERRARI_IMG})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-ink/70 via-ink/30 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-transparent to-transparent" />
      </motion.div>

      <motion.div
        style={{ opacity }}
        className="relative z-10 max-w-[1600px] mx-auto px-6 md:px-12 h-full flex flex-col justify-between py-16 md:py-24"
      >
        <div className="text-cream/70 text-[11px] tracking-[0.35em] flex items-center gap-4">
          <span className="w-8 h-[1px] bg-cream/50" />
          INTERLUDE · A NOTE FROM THE BROKER
        </div>

        <div className="max-w-3xl">
          <motion.blockquote
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-120px" }}
            transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
            className="font-serif text-cream text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-light leading-[1.05] tracking-tight"
          >
            &ldquo;The red car doesn't matter.
            <br />
            <span className="italic text-gold">
              What you leave behind does.
            </span>
            &rdquo;
          </motion.blockquote>
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1, delay: 0.6 }}
            className="mt-8 md:mt-12 text-cream/70 text-[11px] tracking-[0.3em]"
          >
            — DYLAN WEBB, PRINCIPAL BROKER
          </motion.div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl">
          {[
            ["$1.2B", "COVERAGE PLACED"],
            ["48hr", "AVG. APPROVAL"],
            ["0", "CAPTIVE CARRIERS"],
            ["100%", "PICKS UP THE PHONE"],
          ].map(([n, l]) => (
            <div key={l} className="border-t border-cream/25 pt-3">
              <div className="font-serif text-3xl md:text-4xl text-cream tabular-nums font-light">{n}</div>
              <div className="text-[10px] tracking-[0.25em] text-cream/60 mt-2">{l}</div>
            </div>
          ))}
        </div>
      </motion.div>
    </section>
  );
}
